/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  GamoraFoundationAdapter,
  GamoraFoundationAdapterQuery,
  GamoraFoundationAdapterQueryChain,
  GamoraFoundationAdapterQueryChainOperand,
  GamoraFoundationAdapterQueryClear,
  GamoraFoundationAdapterQueryClearOperand,
  GamoraFoundationAdapterQueryGet,
  GamoraFoundationAdapterQueryGetOperand,
  GamoraFoundationAdapterQueryHas,
  GamoraFoundationAdapterQueryHasOperand,
  GamoraFoundationAdapterQueryLock,
  GamoraFoundationAdapterQueryLockOperand,
  GamoraFoundationAdapterQueryOperator,
  GamoraFoundationAdapterQueryRemove,
  GamoraFoundationAdapterQueryRemoveOperand,
  GamoraFoundationAdapterQueryUnlock,
  GamoraFoundationAdapterQueryUnlockOperand,
  GamoraFoundationAdapterQueryUpsert,
  GamoraFoundationAdapterQueryUpsertOperand,
} from '../foundation/index.js';
import { assert } from '../utils/assert.js';
import { debugCreate } from '../utils/debug.js';
import { GamoraErrorNotImplemented } from '../errors/not-implemented.error.js';
import { GamoraUUIDGuard } from '../guards/uuid.js';
const debug = debugCreate({ namespace: 'gamora:adapters:memory' });

/**
 * @public
 */
export type GamoraAdapterMemoryDatabaseNamespace = Map<string, unknown>;

/**
 * @public
 */
export type GamoraAdapterMemoryDatabase = Map<
  string,
  GamoraAdapterMemoryDatabaseNamespace
>;

/**
 * @public
 */
export type GamoraAdapterMemoryLockDatabaseNamespace = Set<string>;

/**
 * @public
 */
export type GamoraAdapterMemoryLockDatabase = Map<
  string,
  GamoraAdapterMemoryLockDatabaseNamespace
>;

/**
 * Let keys expire
 */
export type GamoraAdapterMemoryArgs = {
  keyTTL?: number;
};

/**
 * Memory adapter
 * @public
 */
export class GamoraAdapterMemory<
  TSubject = any,
> extends GamoraFoundationAdapter<TSubject, GamoraAdapterMemoryArgs> {
  private _$db: GamoraAdapterMemoryDatabase;
  private _$locks: GamoraAdapterMemoryLockDatabase;

  constructor(args: GamoraAdapterMemoryArgs = {}) {
    super(args);
    // Create new memory database
    this._$db = new Map();
    this._$locks = new Map();

    // Set args
    this._$args = args;

    // Start the strand
    this.$strand.start();
  }

  /**
   * Create key timeout
   */
  private _createKeyTTL(namespace: string, uuid: GamoraUUIDGuard) {
    if (this._$args.keyTTL) {
      setTimeout(() => {
        try {
          this._executeTransactionSync(
            GamoraFoundationAdapterQueryRemove.fromOperand([namespace, uuid]),
          );
          debug.log(
            '(_createKeyTTL): Removed key for namespace: ' +
              namespace +
              ' with uuid: ' +
              uuid.toString() +
              '.',
          );
        } catch (e) {
          return;
        }
      }, this._$args.keyTTL);
    }
  }

  /**
   * Create lock timeout
   */
  private _createLockTTL(
    namespace: string,
    uuid: GamoraUUIDGuard,
    ttl: number,
  ) {
    // Set TTL
    setTimeout(() => {
      try {
        if (
          this._executeTransactionSync(
            GamoraFoundationAdapterQueryUnlock.fromOperand([namespace, uuid]),
          )
        ) {
          debug.log(
            '(_createLockTTL): Removed lock for namespace: ' +
              namespace +
              ' with uuid: ' +
              uuid.toString() +
              '.',
          );
        }
      } catch (e) {
        return;
      }
    }, ttl);
  }

  /**
   * Internal executor
   */
  private _executeTransactionSync<TQuery extends GamoraFoundationAdapterQuery>(
    query: TQuery,
  ): unknown {
    switch (query.operator) {
      case GamoraFoundationAdapterQueryOperator.LOCK: {
        assert(
          query instanceof GamoraFoundationAdapterQueryLock,
          'Invalid query argument: not a GamoraFoundationAdapterQueryLock.',
        );

        // Get operands
        const [namespace, uuid, ttl] =
          query.operands as GamoraFoundationAdapterQueryLockOperand;
        assert(
          typeof ttl === 'number' && ttl > 0,
          'Invalid TTL in query: not a valid number.',
        );

        // Create lock
        try {
          // Create namespace if not exists
          if (!this._$locks.has(namespace)) {
            this._$locks.set(namespace, new Set());
          }

          // Get the namespace db
          const namespacedDatabase = this._$locks.get(
            namespace,
          ) as GamoraAdapterMemoryLockDatabaseNamespace;

          // Set lock
          assert(
            !namespacedDatabase.has(uuid.toJSON()),
            'Invalid query lock: uuid does not exist.',
          );
          namespacedDatabase.add(uuid.toJSON());

          // Set TTL
          this._createLockTTL(namespace, uuid, ttl);

          // Lock successful
          return true;
        } catch (e) {
          return false;
        }
        break;
      }
      case GamoraFoundationAdapterQueryOperator.UNLOCK: {
        assert(
          query instanceof GamoraFoundationAdapterQueryUnlock,
          'Invalid query argument: not a GamoraFoundationAdapterQueryUnlock.',
        );

        // Get operands
        const [namespace, uuid] =
          query.operands as GamoraFoundationAdapterQueryUnlockOperand;
        try {
          // Get the namespace db
          assert(
            this._$locks.has(namespace),
            'Invalid query unlock: namespace does not exist.',
          );
          const namespacedDatabase = this._$locks.get(
            namespace,
          ) as GamoraAdapterMemoryLockDatabaseNamespace;

          // Ensure lock exists
          assert(
            namespacedDatabase.has(uuid.toJSON()),
            'Invalid query unlock: uuid does not exist.',
          );

          // Remove entry
          namespacedDatabase.delete(uuid.toJSON());

          // Remove namespace if empty
          if (namespacedDatabase.size === 0) {
            this._$locks.delete(namespace);
          }

          // Unlock successful
          return true;
        } catch (e) {
          return false;
        }
        break;
      }
      case GamoraFoundationAdapterQueryOperator.CLEAR: {
        assert(
          query instanceof GamoraFoundationAdapterQueryClear,
          'Invalid query argument: not a GamoraFoundationAdapterQueryClear.',
        );

        // Get operands
        const [namespace] =
          query.operands as GamoraFoundationAdapterQueryClearOperand;

        // Get the namespace db
        assert(
          this._$db.has(namespace),
          'Invalid query clear: namespace does not exist.',
        );
        const namespacedDatabase = this._$db.get(
          namespace,
        ) as GamoraAdapterMemoryDatabaseNamespace;
        namespacedDatabase.clear();
        return true;
        break;
      }
      case GamoraFoundationAdapterQueryOperator.GET: {
        assert(
          query instanceof GamoraFoundationAdapterQueryGet,
          'Invalid query argument: not a GamoraFoundationAdapterQueryGet.',
        );

        // Get operands
        const [namespace, uuid] =
          query.operands as GamoraFoundationAdapterQueryGetOperand;

        // Get the namespace db
        assert(
          this._$db.has(namespace),
          'Invalid query get: namespace does not exist.',
        );
        const namespacedDatabase = this._$db.get(
          namespace,
        ) as GamoraAdapterMemoryDatabaseNamespace;

        // Get the value
        assert(
          namespacedDatabase.has(uuid.toJSON()),
          'Invalid query get: uuid does not exist.',
        );
        return namespacedDatabase.get(uuid.toJSON());
        break;
      }
      case GamoraFoundationAdapterQueryOperator.HAS: {
        assert(
          query instanceof GamoraFoundationAdapterQueryHas,
          'Invalid query argument: not a GamoraFoundationAdapterQueryHas.',
        );

        // Get operands
        const [namespace, uuid] =
          query.operands as GamoraFoundationAdapterQueryHasOperand;

        // Get the namespace db
        if (this._$db.has(namespace)) {
          const namespacedDatabase = this._$db.get(
            namespace,
          ) as GamoraAdapterMemoryDatabaseNamespace;
          return namespacedDatabase.has(uuid.toJSON());
        }
        return false;
        break;
      }
      case GamoraFoundationAdapterQueryOperator.REMOVE: {
        assert(
          query instanceof GamoraFoundationAdapterQueryRemove,
          'Invalid query argument: not a GamoraFoundationAdapterQueryRemove.',
        );

        // Get operands
        const [namespace, uuid] =
          query.operands as GamoraFoundationAdapterQueryRemoveOperand;

        // Get the namespace db
        assert(
          this._$db.has(namespace),
          'Invalid query remove: namespace does not exist.',
        );
        const namespacedDatabase = this._$db.get(
          namespace,
        ) as GamoraAdapterMemoryDatabaseNamespace;

        // Remove entry
        namespacedDatabase.delete(uuid.toJSON());

        // Remove namespace if empty
        if (namespacedDatabase.size === 0) {
          this._$db.delete(namespace);
        }
        return true;
        break;
      }
      case GamoraFoundationAdapterQueryOperator.UPSERT: {
        assert(
          query instanceof GamoraFoundationAdapterQueryUpsert,
          'Invalid query argument: not a GamoraFoundationAdapterQueryUpsert.',
        );

        // Get operands
        const [namespace, uuid, subject] =
          query.operands as GamoraFoundationAdapterQueryUpsertOperand<unknown>;

        // Create namespace if not exists
        if (!this._$db.has(namespace)) {
          this._$db.set(namespace, new Map());
        }

        // Get the namespace db
        const namespacedDatabase = this._$db.get(
          namespace,
        ) as GamoraAdapterMemoryDatabaseNamespace;

        // Set entry
        namespacedDatabase.set(uuid.toJSON(), subject);

        // Set TTL
        this._createKeyTTL(namespace, uuid);
        return true;
        break;
      }
      default: {
        throw new GamoraErrorNotImplemented();
      }
    }
  }

  /**
   * Transaction implementation
   */
  async $transaction<TQuery extends GamoraFoundationAdapterQuery>(
    query: TQuery,
  ): Promise<unknown> {
    assert(
      query instanceof GamoraFoundationAdapterQuery,
      'Invalid query argument: not a GamoraFoundationAdapterQuery.',
    );
    return this.$strand.post(async () => {
      switch (query.operator) {
        case GamoraFoundationAdapterQueryOperator.CHAIN: {
          assert(
            query instanceof GamoraFoundationAdapterQueryChain,
            'Invalid query argument: not a GamoraFoundationAdapterQueryChain.',
          );

          // Get operands
          const queries =
            query.operands as GamoraFoundationAdapterQueryChainOperand;

          // Execute all queries internally
          const results = [];
          for (const query of queries) {
            results.push(this._executeTransactionSync(query));
          }

          // Return the results
          return results;
        }
        default: {
          // Forward to sync helper
          return this._executeTransactionSync(query);
        }
      }
    });
  }
}
